"use client";

import Image from "next/image";
import {useImageSizes} from "@core/helpers/client/contexts/ImageSizesContext";
import {useCallback, useMemo, useState} from "react";
import {useWebSiteContext} from "@core/helpers/client/contexts/WebSiteContext";
import {
	image_priority_props,
	resolve_image_priority,
	shows_skeleton,
	useImageHidden,
	useImagePriority,
} from "@core/helpers/client/contexts/ImagePriorityContext";

const NoImage = ({width, height, style, options, custom_default_svg}) => {
	const more_styles = useMemo(() => {
		const _styles = {...style};
		delete _styles?.width;
		delete _styles?.height;
		return _styles;
	}, [style]);

	return (
		<div
			id="no-image"
			style={{
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				width: style?.width || `${width}px`,
				height: style?.height || `${height}px`,
				...more_styles,
			}}
		>
			{custom_default_svg ? (
				custom_default_svg
			) : (
				<svg
					fill={options?.default_svg_fill || "#5a5a5a"}
					stroke={options?.default_svg_stroke || "none"}
					width={options?.default_svg_width || "50px"}
					height={options?.default_svg_height || "50px"}
					viewBox="0 0 32 32"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M30,3.4141,28.5859,2,2,28.5859,3.4141,30l2-2H26a2.0027,2.0027,0,0,0,2-2V5.4141ZM26,26H7.4141l7.7929-7.793,2.3788,2.3787a2,2,0,0,0,2.8284,0L22,19l4,3.9973Zm0-5.8318-2.5858-2.5859a2,2,0,0,0-2.8284,0L19,19.1682l-2.377-2.3771L26,7.4141Z"/>
					<path
						d="M6,22V19l5-4.9966,1.3733,1.3733,1.4159-1.416-1.375-1.375a2,2,0,0,0-2.8284,0L6,16.1716V6H22V4H6A2.002,2.002,0,0,0,4,6V22Z"/>
				</svg>
			)}
		</div>
	);
};

const myLoader = ({src, width, quality}) =>
	`${src}?w=${width}&q=${quality || 75}`;

// The CDN (assets.shakheara.com, nginx + S3) ignores `w` and `q`, so `myLoader` always downloaded the original file
// (a 166KB PNG for an 80px thumbnail). Images now go through Next's optimizer, which resizes to the width the browser
// picked from `sizes` and serves AVIF/WebP. The URL is built here instead of using next/image's default loader: that
// one throws in development for a host missing from `images.remotePatterns`; this one never throws, and a refused
// host answers 400, which `onError` turns into a retry with `myLoader` (the old direct URL).
// The optimizer path comes from the site's build config: with `trailingSlash: true` Next sets it to "/_next/image/", and
// a hard-coded "/_next/image" made every image answer 308 first.
const image_opts = process.env.__NEXT_IMAGE_OPTS;
const optimizer_path = image_opts?.path || "/_next/image";
const optimizer_loader = ({src, width, quality}) =>
	`${optimizer_path}?url=${encodeURIComponent(src)}&w=${width}&q=${quality || 75}`;

// Only the site's own images are optimized: files under its storage prefix and local `/...` paths. Third-party images
// (trust seals such as eNamad check the visitor's referrer, other hosts may not be in `remotePatterns`) stay direct from
// the browser, exactly as before. SVG (refused by the optimizer), GIF (animation is kept as is) and data/blob URLs too.
const is_direct_only = (src, storage_prefix) => {
	// a static import (StaticImageData) is a local file under /_next/static: optimize it
	if (src && typeof src === "object") return false;
	if (!src || /\.(svg|gif)(\?|#|$)/i.test(src) || /^(data|blob):/i.test(src)) return true;
	const is_local = src.startsWith("/") && !src.startsWith("//");
	const is_storage = !!storage_prefix && src.startsWith(storage_prefix);
	return !(is_local || is_storage);
};

const is_valid_size = (value) => Number.isFinite(Number(value)) && Number(value) > 0;

const ImageComponent = ({
	                        src,
	                        placeholder_src,
	                        style,
	                        width,
	                        height,
	                        default_src_options = {},
	                        custom_default_svg,
	                        linear_gradient,
	                        image_className,
	                        alt,
	                        // legacy switch: true → "lcp", false → "lazy"; not passed → ImagePriorityProvider, then "lcp"
	                        isLCP,
	                        priority_level,
	                        // plain next/image priority props still work: they are folded into the level
	                        priority,
	                        preload,
	                        loading,
	                        fetchPriority,
	                        // taken out of `props` so an explicit undefined does not wipe the slider / default value
	                        sizes,
	                        default_complete_load_img = false,
	                        // the prefix for a relative `src`; the site's own prefix when not given (it used to leak onto <img>)
	                        storage_prefix_url,
	                        ...props
                        }) => {
	const {
		web_site_setting: {web_site_storage_prefix_url: site_prefix},
	} = useWebSiteContext();
	const web_site_storage_prefix_url = storage_prefix_url || site_prefix;
	const level = resolve_image_priority({
		hidden: useImageHidden(),
		priority_level,
		next_props: {priority, preload, loading, fetchPriority},
		isLCP,
		context: useImagePriority(),
	});
	// a slider or grid around this image may know its column width (ImageSizesProvider); an explicit `sizes` prop wins
	const context_sizes = useImageSizes();

	const full_src = useMemo(() => {
		const _src = src || placeholder_src;
		if (!_src) return null;
		// static import (StaticImageData): next/image takes the object as is
		if (typeof _src === "object") return _src;
		if (_src.startsWith("http") || _src.startsWith("/")) return _src;
		return `${web_site_storage_prefix_url}/${_src}`;
	}, [src, placeholder_src, web_site_storage_prefix_url]);
	const [error, set_error] = useState(false);
	const [complete_load_img, set_complete_load_img] = useState(
		default_complete_load_img,
	);
	// the source whose optimized URL failed; that source is then loaded directly from the CDN as before
	const [direct_src, set_direct_src] = useState(null);
	const use_direct = direct_src === full_src || is_direct_only(full_src, web_site_storage_prefix_url);

	// `fill`, or no numeric width/height: the box fills its parent (was `undefinedpx`, so the box had no height and
	// the image did not show; ciseco). The parent needs a height (aspect-ratio or a fixed height).
	const use_fill = !!props.fill || !(is_valid_size(width) && is_valid_size(height));

	// an image the browser already finished before hydration fires no onLoad that React sees
	const mark_if_complete = useCallback((node) => {
		if (node?.complete && node.naturalWidth > 0) set_complete_load_img(true);
	}, []);

	if (!full_src && !error) {
		return (
			<NoImage
				style={use_fill ? {width: "100%", height: "100%", ...style} : style}
				options={default_src_options}
				custom_default_svg={custom_default_svg}
				width={width}
				height={height}
			/>
		);
	}

	if (error) {
		return placeholder_src ? (
			<ImageComponent
				src={placeholder_src}
				sizes={sizes}
				style={style}
				width={width}
				height={height}
				default_src_options={default_src_options}
				custom_default_svg={custom_default_svg}
				image_className={image_className}
				alt={alt}
				{...props}
			/>
		) : (
			<NoImage
				style={style}
				options={default_src_options}
				custom_default_svg={custom_default_svg}
				width={width}
				height={height}
				{...props}
			/>
		);
	}

	return (
		<div
			style={use_fill ? {
				position: "relative",
				isolation: "isolate",
				width: "100%",
				height: "100%",
				...style,
			} : {
				position: "relative",
				isolation: "isolate",
				width: style?.width || `${width}px`,
				height: style?.height || `${height}px`,
				...style,
			}}
		>
			{/* the skeleton sits BEHIND the image (z-index -1 inside this isolated box): a loaded image covers it straight
			    from the server HTML, without waiting for hydration + onLoad (it used to sit on top and hid the hero until
			    JS ran). Only for lazy images; the ones in the first viewport show no loading state at all. */}
			{shows_skeleton(level) && (
				<div
					className="shimmer-animation"
					aria-hidden="true"
					style={{
						position: "absolute",
						inset: 0,
						width: "100%",
						height: "100%",
						zIndex: -1,
						opacity: complete_load_img ? 0 : 1,
					}}
				/>
			)}

			<Image
				ref={mark_if_complete}
				loader={use_direct ? myLoader : optimizer_loader}
				style={style}
				src={full_src}
				alt={alt || ""}
				className={image_className}
				{...(use_fill ? {fill: true} : (typeof full_src === "object" ? {} : {width, height}))}
				quality={75}
				placeholder="empty"
				sizes={sizes || context_sizes || "(max-width:768px)100vw,886px"}
				{...image_priority_props(level)}
				onError={() => {
					// first failure of an optimized URL: try the same image directly before showing the fallback
					if (!use_direct) {
						set_direct_src(full_src);
						return;
					}
					set_error(true);
					set_complete_load_img(true);
				}}
				onLoad={() => {
					set_complete_load_img(true);
				}}
				{...props}
			/>

			{linear_gradient && (
				<div
					style={{
						position: "absolute",
						inset: 0,
						background: linear_gradient,
						pointerEvents: "none",
					}}
				/>
			)}
		</div>
	);
};

export default ImageComponent;
